import { Box, Link, Paper, useTheme } from '@mui/material'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useFormik } from 'formik'
import { enqueueSnackbar } from 'notistack'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { BackContinue } from '@components/back_continue/back_continue'
import { i18WithComponent as t } from '@shared/locale'
import { detailsPoliciesSchema, getProfileValues, setupInitialValues } from './details_policies_schema'
import { ContactInformation } from '@components/profile_form/form'
import { saveSMSConsent } from '@components/profile_form/save_sms_consent'
import { useBookingContext } from '@providers/booking'
import { type TCardEntry, FlexCardDisplay } from 'components-ui/src/flex_card_display/flex_card_display'
import { FlexCardField } from 'components-ui/src/flex_card_input/flex_card_field'
import { Spacer } from 'components-ui/src/spacer/spacer'
import { ClientBookingReservationService } from 'service-api/src/services/client_booking_reservation.service'
import { ClientLocationUserProfileService } from 'service-api/src/services/client_location_user_profiles.service'
import { useLocationContext } from '@providers/location'
import { useSessionContext } from '@providers/session'
import { STICKY_FOOTER_HEIGHT } from '@shared/constants'
import { buildErrorMessage } from '@shared/error'
import { backNavigation, continueNavigation } from '@shared/header_navigation'
import {
  bookingConfigsFromReservation,
  mergeBookingConfigPreferSearchHours,
} from '@shared/surcharge_policy_arrival_adapters'
import { useRoutes } from 'app/use_routes'
import { SurchargePolicy } from 'components-ui/src/purchase_summary/surcharge_policy/surcharge_policy'
import { shallowEqual } from 'components-ui/src/shared/common'
import { isVaccinationEnabled, isVaccinationRequired, isVetEnabled } from 'components-ui/src/shared/location'
import { captureError } from 'service-api/src/shared/monitoring'
import { TState } from 'service-api/src/shared/states'
import { BookingReservationClientDto } from 'service-api/src/types/api'
import { buildOrderInput, inputCardGroupsBuilder } from './helper'
import { VaccinationRecords } from './vaccination_records'
import { VeterinarianInformation } from './veterinarian_information'

const ENABLE_VAX_VET = false

export const DetailsPoliciesForm = ({ reservation }: { reservation: BookingReservationClientDto }) => {
  const bookingCtx = useBookingContext()
  const sessionCtx = useSessionContext()
  const { location } = useLocationContext()
  const { accountName: locationName = '' } = useParams()
  const theme = useTheme()
  const renderStyle = useMediaQuery(theme.breakpoints.down('md')) ? 'mobile' : 'desktop'
  const isMobile = renderStyle === 'mobile'
  const { navigateRoute } = useRoutes()
  const serviceTypeName = reservation.order?.primaryServiceType?.serviceType?.name ?? ''
  const lupId = sessionCtx.state.user?.lupId
  const [submitCount, setSubmitCount] = useState(0)
  const fieldRefs = useRef<Record<string, HTMLElement | null>>({})

  const inputCardGroups = useMemo(
    () => (serviceTypeName === 'packages' ? [] : inputCardGroupsBuilder(reservation.flexBookingConfs || [])),
    [reservation.flexBookingConfs, serviceTypeName]
  )

  const formik = useFormik<ReturnType<typeof setupInitialValues>>({
    initialValues: setupInitialValues(bookingCtx.state.cart),
    validationSchema: detailsPoliciesSchema(reservation, inputCardGroups),
    onSubmit: async (values): Promise<boolean> => {
      const data = buildOrderInput(values, reservation, inputCardGroups, bookingCtx.state.cart)
      const initialProfile = getProfileValues(formik.initialValues)
      const updatedProfile: GenericSimpleBag = getProfileValues(values)

      const isProfileUpdated = !shallowEqual(initialProfile, updatedProfile)

      try {
        if (isProfileUpdated) {
          await ClientLocationUserProfileService.put(locationName, lupId || '', updatedProfile)
        }

        let smsConsentStatus = bookingCtx.state.cart.contact.smsConsentStatus
        if (values.consentToSMS && smsConsentStatus !== 'ACTIVE') {
          const confirmed = await saveSMSConsent(locationName, {
            lupId: lupId || '',
            phoneNumber: values.phone || '',
            operatorEmail: sessionCtx.state.user?.email || '',
          })
          if (confirmed) {
            smsConsentStatus = 'ACTIVE'
            // Preserve confirmation even if the reservation update fails and is retried.
            bookingCtx.dispatch({
              type: 'SET_CONTACT_FIELD',
              payload: {
                value: {
                  fieldName: 'smsConsentStatus',
                  value: smsConsentStatus,
                },
              },
            })
          }
        }

        const order = await ClientBookingReservationService.updateReservation(
          reservation.order!.id!,
          locationName,
          data
        )
        bookingCtx.dispatch({
          type: 'SET_PAYMENT_CONTACT',
          payload: { value: { ...values, smsConsentStatus } },
        })
        bookingCtx.dispatch({
          type: 'SET_RESERVATION_DATA',
          payload: { value: order },
        })
        return true
      } catch (err) {
        enqueueSnackbar(buildErrorMessage(err), { variant: 'error' })
        captureError(err as Error)
        return false
      }
    },
  })

  const onContactChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, id, dataset, value } = event.target
    bookingCtx.dispatch({
      type: 'SET_CONTACT_FIELD',
      payload: {
        value: { nspace: dataset?.nspace, fieldName: name || id, value },
      },
    })
    formik.handleChange(event)
  }

  const onStateSelect = (_event: React.SyntheticEvent, value: TState | null) => {
    const stateCode = value?.code ?? ''
    bookingCtx.dispatch({
      type: 'SET_CONTACT_FIELD',
      payload: {
        value: {
          nspace: 'mailingAddress',
          fieldName: 'state',
          value: stateCode,
        },
      },
    })
    formik.setFieldValue('state', stateCode)
  }

  useEffect(() => {
    if (formik.submitCount > submitCount && Object.keys(formik.errors).length > 0) {
      const firstErrorField = Object.keys(formik.errors)[0]
      const errorElement = fieldRefs.current[firstErrorField]

      if (errorElement instanceof HTMLElement) {
        errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
        errorElement.focus?.()
      }
    }
    setSubmitCount(formik.submitCount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.submitCount, formik.errors])

  const onBackHandler = useCallback(
    async (ev?: React.SyntheticEvent) => {
      ev?.preventDefault()
      const route = await backNavigation(bookingCtx)
      if (route) navigateRoute(route, { serviceTypeName, accountName: locationName })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navigateRoute, serviceTypeName]
  )

  const onContinueHandler = useCallback(
    async (ev?: React.SyntheticEvent) => {
      ev?.preventDefault()
      if (await formik.submitForm()) {
        const route = await continueNavigation(bookingCtx, locationName)
        if (route) {
          navigateRoute(route, { serviceTypeName })
        }
      }

      // always returns false because we are handling the navigation too
      // this way short-circuit the BackContinue navigation handling
      return false
    },
    [locationName, bookingCtx, formik, navigateRoute, serviceTypeName]
  )

  const reservationBookingConfig = useMemo(() => bookingConfigsFromReservation(reservation), [reservation])
  const surchargeBookingConfig = useMemo(
    () => mergeBookingConfigPreferSearchHours(reservationBookingConfig, bookingCtx.state.config.booking),
    [reservationBookingConfig, bookingCtx.state.config.booking]
  )

  const hasSurchargePolicy = surchargeBookingConfig?.locationBookingConf?.surchargeStrategy !== 'DISABLED'

  const policies = useMemo(() => {
    const policyByGroup = (reservation.flexBookingConfs || []).reduce<Record<string, TCardEntry[]>>((res, c) => {
      if (
        c.group &&
        c.target === 'ORDER' &&
        (c.step === 'BOOKING' || c.step === 'ALL') &&
        (c.type === 'INFO' || c.type === 'URL')
      ) {
        const groupedList = res?.[c.group] ?? []
        return {
          ...res,
          [c.group]: [...groupedList, c],
        }
      }
      return res
    }, {})
    return Object.values(policyByGroup)
  }, [reservation])

  // NOTE: the formId is used with the header's Continue button, to be able to submit
  // a form from outside the component tree
  return (
    <form id="details-policies-form" action="" onSubmit={onContinueHandler} style={{ width: '100%' }}>
      <Stack direction={{ sx: 'column', md: 'row' }} alignItems="start" spacing={2} flex={1} mb={2}>
        <Stack direction="column" spacing={3} flex={1} width="100%">
          <Paper
            elevation={1}
            sx={{
              p: 2,
              pb: 3,
              width: '100%',
              borderRadius: `${isMobile ? 0 : theme.shape.borderRadius}px`,
            }}
          >
            <Typography variant="h5">{t('detailsPolicies.contactInformation')}</Typography>
            <Spacer height={3} />
            <ContactInformation
              formik={formik}
              onChange={onContactChange}
              onChangeSelect={onStateSelect}
              size="small"
              smsConsent={{
                checked: formik.values.consentToSMS,
                confirmed: bookingCtx.state.cart.contact.smsConsentStatus === 'ACTIVE',
                isSaving: formik.isSubmitting,
                locationName: location?.displayName || '',
                onChange: (checked) => formik.setFieldValue('consentToSMS', checked),
              }}
              fieldRefs={fieldRefs}
            />
          </Paper>
          {ENABLE_VAX_VET && isVetEnabled(reservation.locationBookingConf!) && (
            <VeterinarianInformation formik={formik} fieldRefs={fieldRefs} />
          )}
          {ENABLE_VAX_VET && isVaccinationEnabled(reservation.locationBookingConf!) && (
            <VaccinationRecords
              required={isVaccinationRequired(reservation.locationBookingConf!)}
              reservation={reservation}
            />
          )}
          {inputCardGroups.map((group, idx) => {
            return formik && <FlexCardField key={idx} fields={group} formik={formik} fieldRefs={fieldRefs} />
          })}
        </Stack>
        <Stack direction="column" spacing={3} flex={1} mt={isMobile ? 3 : 0} width="100%">
          {policies.map((policy, idx) => (
            <FlexCardDisplay key={idx} cardData={policy} />
          ))}
          <Box>
            {reservation.order && surchargeBookingConfig && hasSurchargePolicy && (
              <SurchargePolicy order={reservation.order} bookingConfig={surchargeBookingConfig} location={location} />
            )}
          </Box>
          <Box sx={{ padding: isMobile ? 2 : 0 }}>
            <Typography variant="body1" sx={{ opacity: 0.8 }} textAlign="left">
              {t('detailsPolicies.messageConsent', {
                facilityName: location?.displayName || '',
                terms: (
                  <Link target="_blank" href={`/${locationName}/privacy-policy`} key="terms">
                    {t('detailsPolicies.terms')}
                  </Link>
                ),
              })}
            </Typography>
            <Spacer height={2} />
            <Typography variant="body1" sx={{ opacity: 0.8 }} textAlign="left">
              {t('detailsPolicies.confirmation', {
                termConditionsLink: (
                  <Link target="_blank" href={'https://www.goose.pet/terms-conditions'} key="terms">
                    {t('detailsPolicies.tcAnchorText')}
                  </Link>
                ),
              })}
            </Typography>
          </Box>
        </Stack>
      </Stack>

      <Spacer height={STICKY_FOOTER_HEIGHT} />
      <BackContinue
        continueLabel={t('detailsPolicies.continueButton') as string}
        onContinue={onContinueHandler}
        onBack={onBackHandler}
      />
    </form>
  )
}
